import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import { useSessionUser } from '@contexts/SessionUserContext';
import TermsAndConditionsPdf from '@documents/terms_and_conditions_sept_23_2024.pdf';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Checkbox from 'antd/es/checkbox';
import Divider from 'antd/es/divider';
import Layout from 'antd/es/layout';
import Modal from 'antd/es/modal';
import { type ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';

import CenterSpin from './CenterSpin';
import MarkdownContent from './MarkdownContent';
import TopBar from './TopBar';

import {
  acceptUserTermsAndConditions,
  useUserTermsAndConditionsAcceptancesQuery,
} from '@queries/users';

const StyledLayout = styled(Layout)`
  height: 100vh;
  scrollbar-width: thin;
  scrollbar-color: grey ${(props) => props.theme.token.colorBgContainer};
  flex-direction: column;
`;

const ContentLayout = styled(Layout)`
  flex-direction: row;
`;

const StyledDivider = styled(Divider)`
  width: 100%;
  padding: 0;
  margin: 0;
`;

const TermsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TermsContent = styled.div`
  height: 500px;
  overflow-y: auto;
  border: 1px solid ${(props) => props.theme.token.colorBorderSecondary};
  border-radius: ${(props) => props.theme.token.borderRadiusLG}px;
  padding: 16px;
  position: relative;
`;

const DownloadLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const AcceptanceFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const AcceptButton = styled(Button)`
  align-self: flex-end;
`;

const TermsAndConditionsMarkdown = `# MODERATELY AI INC.

## Terms and Conditions

**Moderately AI Inc.**  
**Terms & Conditions**

These Terms and Conditions, your subscription, and any additional applicable terms constitute the complete agreement (the **"Agreement"**) and supersede any prior discussions, agreements, representations, or warranties.

**Definitions:**
- **"Moderately AI," "we," "us"** includes Moderately AI Inc. and its affiliates.
- **"You," "your"** means the client, customer, or subscriber.

---

## 1. Products

### 1.1 Products and Updates
- Products, services, software, and data may change periodically.
- Fundamental nature won't change unless required by third-party agreements or compliance obligations.

### 1.1 Updates
Products, services, and software may update without changing their fundamental nature unless necessitated by third-party agreements or compliance requirements.

### 1(c). Password Security
You are responsible for maintaining the confidentiality of your passwords and must promptly report unauthorized access.

### 1(d). Unauthorized Technology
Prohibited activities without prior approval:
- Installation or running of software/hardware on our systems
- Automated data scraping, mining, indexing, or downloading.

### 1(e). Data Use
We may collect and share usage data (**"your information"**) with third-party providers for purposes like compliance, protection, rights enforcement, and recommending products/services.

### 1(f). Third-Party Terms
You must comply with third-party provider terms included in our services.

### 1(g). Use Restrictions
You must not:
- Create derivative or competitive products.
- Reverse engineer, mine, scrape, or index our data.
- Provide third-party access to our products.

### 1(h). Storage Limits
Monthly limits:
- 1 TB data egress
- 1 TB uploaded files

Exceeding these limits may incur additional charges or suspension.

### 1(i). Security
You must use industry-standard safeguards to protect data security.

---

## 2. Information Services

### 2(a). License
Licensed for internal business use only.

### 2(b). Third-Party Distribution
Limited disclosure permitted only to advisers or necessary third parties.

---

## 3. Software

### 3(a). License
Permitted for internal business use, including updates and specified releases.

### 3(b). Delivery
Online software delivery constitutes acceptance of usage terms.

---

## 4. Charges

### 4(a). Fees and Subscription
Subscriptions purchased via [www.moderately.ai](https://www.moderately.ai).

### 4(b). Automatic Charging
You authorize automatic billing to your designated account.

### 4(c). Changes to Charges
Moderately AI may adjust fees with 30 days' notice. Significant fee increases (>15%) allow subscription termination.

---

## 5. Privacy

### 5(a). Data Protection
Both parties agree to comply with privacy laws regarding personal information.

### 5(b). Security Measures
Technical safeguards are implemented to protect personal information, with retention only as legally permitted or required.

---

## 6. Intellectual Property
Moderately AI retains all intellectual property rights in its services, software, and data (the "Deliverables"). Moderately AI assigns Intellectual Property Rights in Deliverables to you, where explicitly agreed upon.

---

## 7. Confidentiality
Confidential information must remain confidential for three years after termination of your subscription or until the information is no longer considered confidential.

---

## 8. Warranties and Disclaimers
Exclusive warranties provided by Moderately AI:

### 8(a). Exclusions
No warranties or guarantees are given regarding accuracy, interruptions, errors, or omissions in the products/services.

### 8(b). Disclaimers
Moderately AI and its third-party providers are not responsible for regulatory fees or decisions/actions based on our products.

### 8(c). No Advice
Moderately AI provides no legal or professional advice.

---

## 9. Limitation of Liability

### 9(a). Liability Cap
Liability capped at fees paid within the previous 12 months.

### 9(b). Exceptions
No liability limit for fraud, negligence resulting in personal harm, or intellectual property infringement.

### 9(c). Claims
Claims must be made within 12 months from discovery.

### 9(d). Your Responsibilities
You are responsible for:
- Proper use of services.
- Compliance with instructions.
- Uploading lawful content.
- Any claims from third-party use through you.

---

## 10. Indemnification
You will indemnify Moderately AI against claims, settlements, or costs arising from your use of products or violations of these terms.

---

## 11. Term & Termination

### 11(a). Term
Effective upon your use of Moderately AI products/services. You may terminate at any time without penalty.

### 11(b). Suspension
We may suspend/deactivate access if terms are breached or services discontinued.

### 11(c). Breach
Immediate termination if material breaches are not rectified within 15 days.

---

## 12. Third-Party Rights
Affiliates/third-party providers benefit from protections in this Agreement. No other third-party rights apply.

---

## 13. General

### 13(a). Assignment
Moderately AI may assign rights/duties under this Agreement with notice.

### 13(b). Feedback
Moderately AI may use feedback provided by you for product improvement.

### 13(c). Severability
Invalid clauses will be minimally modified or removed without affecting remaining terms.

### 13(d). Governing Law
Subject to Canadian and Ontario provincial laws.

### 13(e). Amendment
Changes to terms with notice; termination allowed within 5 days if substantial.

### 13(g). Waiver
Delay or inaction does not waive rights.`;

// Stub component for Terms and Conditions content
const TermsAndConditionsContent = () => {
  return <MarkdownContent content={TermsAndConditionsMarkdown} />;
};

const AuthenticatedLayout = ({ children }: { children: ReactNode }) => {
  const { message } = App.useApp();

  const { userId: sessionUserId, isInitialQueryFinished } = useSessionUser();
  const termsContentRef = useRef<HTMLDivElement>(null);
  const [hasAgreed, setHasAgreed] = useState(false);

  // Check if user has accepted the terms and conditions
  const userTermsAndConditionsAcceptancesQuery =
    useUserTermsAndConditionsAcceptancesQuery(sessionUserId);

  const isTermsAndConditionsAccepted =
    !!userTermsAndConditionsAcceptancesQuery.data?.length;

  const showTermsAndConditionsModal =
    !isTermsAndConditionsAccepted && !!sessionUserId;

  const handleAcceptTerms = async () => {
    if (sessionUserId) {
      try {
        await acceptUserTermsAndConditions(sessionUserId);
        await userTermsAndConditionsAcceptancesQuery.refetch();
      } catch (error) {
        void message.error('Failed to accept terms and conditions');
      }
    }
  };

  const handleDownloadPdf = () => {
    // Create a link element and trigger download
    const link = document.createElement('a');
    link.href = TermsAndConditionsPdf;
    link.download = 'terms_and_conditions.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show loading spinner while initial authentication or data is loading
  if (
    !isInitialQueryFinished ||
    userTermsAndConditionsAcceptancesQuery.isLoading
  ) {
    return <CenterSpin />;
  }

  return (
    <>
      <Modal
        title="Terms and Conditions"
        open={showTermsAndConditionsModal}
        width="min(80%, 900px)"
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
      >
        <TermsContainer>
          <DownloadLink>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPdf}
            >
              Download PDF
            </Button>
          </DownloadLink>

          <TermsContent ref={termsContentRef}>
            <TermsAndConditionsContent />
          </TermsContent>

          <AcceptanceFooter>
            <CheckboxContainer>
              <Checkbox
                checked={hasAgreed}
                onChange={(e) => setHasAgreed(e.target.checked)}
              >
                I have read and agree to the Terms and Conditions, Privacy
                Policy, and consent to the collection and use of my personal
                information as described therein.
              </Checkbox>
            </CheckboxContainer>

            <AcceptButton
              type="primary"
              onClick={handleAcceptTerms}
              disabled={!hasAgreed}
            >
              I Accept
            </AcceptButton>
          </AcceptanceFooter>
        </TermsContainer>
      </Modal>
      <StyledLayout>
        <TopBar />
        <StyledDivider />
        <ContentLayout>{children}</ContentLayout>
      </StyledLayout>
    </>
  );
};

export default AuthenticatedLayout;
